import React, { useEffect, useState } from 'react';
import { OverlayConstants } from '../../../Constants';
import { connect, useDispatch } from 'react-redux';
import { setOverlay } from '../../../redux/slices/appStateSlice';
import { RichText, Text } from '@sitecore-jss/sitecore-jss-react';
import { getLondonTime } from '../../../utils/datetime/getLondonTime';
import InputQuestion from '../../questions/InputQuestion';
import config from '../../../temp/config';
import useRecaptcha from '../../../utils/recaptcha/useRecaptcha';
import { errorMessages } from '../../../utils/errors/constants';
import { pushServiceDownError } from '../../../utils/analytics/datalayer';
import PropTypes from 'prop-types';

const STATES = { success: 'success', failure: 'failure' };
const minimumSuccessTimeout = 5000;

const vdsBodyLocked = 'vds-body-locked';
const Callback = props => {
  const { fields, show, utms, callbackDetails, prospectDetails, quoteDetails, isModalOpen } = props;
  const dispatch = useDispatch();
  const [openState, setOpenState] = useState(false);
  const [errors, setErrors] = useState({});
  const [hasErrors, setHasErrors] = useState(false);
  const [enableSubmit, setEnableSubmit] = useState(false);
  const [displayErrors, setDisplayErrors] = useState(false);
  const [state, setState] = useState(null);
  const [formId, setFormId] = useState('');
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const executeRecaptcha = useRecaptcha();
  const blockingErrorTypes = [errorMessages.EMPTY];
  const raiseEvent = () => {
    if (typeof window !== 'undefined' && fields.eventName?.value) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: fields.eventName?.value,
        callbackID: formId,
      });
    }
  };

  const hideOverlay = () => {
    setErrors({});
    setTouched({});
    if (!isModalOpen) {
      document.body.classList.remove(vdsBodyLocked);
    }
    dispatch(setOverlay(null));
  };

  const calculateOpenState = () => {
    const weekDays = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'],
      now = getLondonTime(),
      today = weekDays[now.getDay()],
      openTime = fields[`${today}Open`]?.value,
      closeTime = fields[`${today}Close`]?.value,
      open = getLondonTime(),
      close = getLondonTime();

    if (!openTime || !closeTime) {
      return false;
    }

    open.setUTCHours(openTime.split(':')[0]);
    open.setUTCMinutes(openTime.split(':')[1]);
    close.setUTCHours(closeTime.split(':')[0]);
    close.setUTCMinutes(closeTime.split(':')[1]);

    return now >= open && now < close;
  };

  const renderState = () => {
    document.body.classList.add(vdsBodyLocked);
    if (state === STATES.success) {
      raiseEvent();
      return (
        <>
          <RichText field={fields.successMessage} data-testid={`${fields.testid?.value}-success`} />
          <button
            type="button"
            onClick={hideOverlay}
            className="vds-button vds-button--fluid"
            data-testid={`${fields.testid?.value}-success-close-cta`}
          >
            Close
          </button>
        </>
      );
    }

    if (state === STATES.failure) {
      return (
        <>
          <RichText field={fields.failureMessage} data-testid={`${fields.testid?.value}-failure`} />
          <button
            type="button"
            onClick={hideOverlay}
            className="vds-button vds-button--fluid"
            data-testid={`${fields.testid?.value}-failure-close-cta`}
          >
            Close
          </button>
        </>
      );
    }

    return (
      <div className="vds-slide-out-drawer__content" id="slideout-callback-form">
        <div
          className="vds-info-panel vds-margin-bottom-spacing-5"
          data-testid={`${fields.testid?.value}-info-panel`}
        >
          <Text
            tag="h2"
            className="vds-type-display-2b vds-margin-bottom-spacing-2"
            field={fields.introTitle}
            data-testid={`${fields.testid?.value}-info-panel-title`}
          />

          <dl className="vds-margin-bottom-spacing-2">
            <Text field={fields.introStrapLine} />
            <dd>
              <a
                className="InfinityNumber clickable cbslideoutphnumber"
                href={fields.contactNumber?.value}
                data-testid="contact-number-link"
              >
                {fields.contactNumberText?.value}
              </a>
            </dd>
          </dl>

          <p
            className="vds-margin-bottom-spacing-2"
            data-testid={`${fields.testid?.value}-status-text`}
          >
            <Text field={fields.openStatus} />
            &nbsp;
            {openState ? (
              <strong className="vds-colour-success">Open</strong>
            ) : (
              <strong className="vds-colour-invalid">Closed</strong>
            )}
          </p>

          <RichText field={fields.introFooter} />
        </div>
        <Text
          tag="h3"
          className="vds-slide-out-drawer__subheading"
          field={fields.formTitle}
          data-testid={`${fields.testid?.value}-form-title`}
        />
        <form
          className="vds-form"
          id="slideout-callback"
          data-testid={`${fields.testid?.value}-form`}
          onSubmit={handleSubmit}
        >
          {renderInputs()}
        </form>
        <div className="vds-form__group vds-form__group--d2c-actions">
          <button
            className={`vds-button vds-button--fluid${
              enableSubmit && !submitting ? '' : ' vds-button--disabled'
            }${submitting ? ' vds-button--loading' : ''}`}
            id="callback-submit"
            data-testid={`${fields.testid?.value}-submit-cta`}
            style={{ cursor: 'pointer', pointerEvents: 'unset' }}
            onClick={handleSubmit}
            disabled={submitting || !enableSubmit}
            aria-busy={submitting}
          >
            <Text field={fields.submitButtonText} />
          </button>
        </div>
        <RichText
          field={fields.formDisclaimer}
          data-testid={`${fields.testid?.value}-disclaimer`}
        />
      </div>
    );
  };

  const renderInputs = () => {
    if (fields.questions?.length >= 1) {
      document.body.classList.add(vdsBodyLocked);

      return fields.questions.map((q, i) => {
        const fieldName = q.fields.name?.value?.toLowerCase();

        const inputProps = {
          onBlur: e => {
            setTouched(prev => ({ ...prev, [fieldName]: true }));
            if (!e.target.value || e.target.value.trim() === '') {
              setDisplayErrors(false);
              setErrors(prev => ({
                ...prev,
                [fieldName]: errorMessages.EMPTY,
              }));
            } else {
              setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[fieldName];
                return newErrors;
              });
            }
          },
        };
        const getError = () => {
          return {
            ...errors[fieldName],
            name: fieldName,
          };
        };
        return (
          <InputQuestion
            key={`callbackQuestion-${i}`}
            rendering={{ uid: 'callback' }}
            fields={q.fields}
            displayErrors={touched[fieldName] || displayErrors}
            error={errors[fieldName]}
            onError={errorType => {
              if (errorType) {
                setErrors(prev => ({ ...prev, [fieldName]: errorType }));
              } else {
                let newErrors = { ...errors };
                delete newErrors[fieldName];
                setErrors(newErrors);
              }
            }}
            currentError={getError()}
            {...inputProps}
          />
        );
      });
    }
  };

  const getFallbackValue = (primary, secondary) => primary || secondary;

  const buildFullName = (details, fallbackDetails) => {
    const forename = details.forename || fallbackDetails?.forename || '';
    const surname = details.surname || fallbackDetails?.surname || '';
    return `${forename} ${surname}`.trim();
  };

  const buildRequestBody = recaptchaToken => {
    let mappedUtms = {};
    Object.keys(utms).forEach(
      key => (mappedUtms[`utm${key.charAt(0).toUpperCase()}${key.slice(1)}`] = utms[key])
    );
    let newFormId = '';
    if (!formId && fields.formIdLength?.value && fields.formIdAcceptedCharacters?.value) {
      const chars = fields.formIdAcceptedCharacters.value.split('');
      while (newFormId.length < fields.formIdLength.value) {
        const rndChar = chars[Math.floor(Math.random() * chars.length)];
        newFormId += rndChar;
      }
      setFormId(newFormId);
    }

    return {
      ...mappedUtms,
      forename: getFallbackValue(callbackDetails?.forename, prospectDetails?.forename),
      surname: getFallbackValue(callbackDetails?.surname, prospectDetails?.surname),
      fullname: buildFullName(callbackDetails, prospectDetails),
      title: getFallbackValue(callbackDetails?.title, prospectDetails?.title),
      telephoneNumber: getFallbackValue(
        callbackDetails?.contactNumber,
        prospectDetails?.telephoneNumber
      ),
      project: fields.callbackProject?.value,
      leadType: fields.callbackLeadType?.value,
      stagingType: fields.callbackStagingType?.value,
      webform:
        quoteDetails?.length > 0
          ? fields.callbackWebFormApply?.value
          : fields.callbackWebFormQuote?.value,
      formId: formId || newFormId,
      recaptchaToken: recaptchaToken,
    };
  };

  const hasRequiredFields = () => {
    return (
      !(callbackDetails?.forename || prospectDetails?.forename) ||
      !(callbackDetails?.surname || prospectDetails?.surname) ||
      !(callbackDetails?.contactNumber || prospectDetails?.telephoneNumber)
    );
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (submitting) {
      return false;
    }
    for (const field in errors) {
      if (errors.hasOwnProperty(field) && errors[field]) {
        setHasErrors(true);
        setDisplayErrors(true);
        return false;
      }
    }

    if (hasRequiredFields()) {
      setHasErrors(true);
      setDisplayErrors(true);
      return false;
    }

    setSubmitting(true);

    let gToken = '';

    if (typeof window !== 'undefined' && config.recaptcha.toggle && window.grecaptcha) {
      gToken = await executeRecaptcha();
    }

    const requestBody = buildRequestBody(gToken);
    return fetch(config.endpoints.callback, {
      method: 'POST',
      body: JSON.stringify(requestBody),
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(res => {
        if (res.ok) {
          setState(STATES.success);
          return;
        }
        pushServiceDownError(config.endpoints.callback, res);
        setState(STATES.failure);
        setSubmitting(false);
      })
      .catch(err => {
        pushServiceDownError(config.endpoints.callback, null, err);
        setState(STATES.failure);
        setSubmitting(false);
      });
  };

  useEffect(() => {
    for (const field in errors) {
      if (
        errors.hasOwnProperty(field) &&
        errors[field] &&
        blockingErrorTypes.includes(errors[field])
      ) {
        setEnableSubmit(false);
        setHasErrors(true);
        return;
      }
    }
    if (hasRequiredFields()) {
      setEnableSubmit(false);
      setHasErrors(true);
      return;
    }

    setEnableSubmit(true);
  }, [errors, callbackDetails, prospectDetails]);

  useEffect(() => {
    setOpenState(calculateOpenState());
    const openStateInterval = setInterval(() => {
      setOpenState(calculateOpenState());
    }, minimumSuccessTimeout);
    return () => clearInterval(openStateInterval);
  }, []);

  useEffect(() => {
    if (
      !callbackDetails.forename ||
      !callbackDetails.surname ||
      (!callbackDetails.contactNumber && prospectDetails)
    ) {
      if (!callbackDetails.forename && prospectDetails.forename) {
        dispatch({
          type: 'quoteData/updateCallbackForename',
          payload: prospectDetails.forename,
        });
      }

      if (!callbackDetails.surname && prospectDetails.surname) {
        dispatch({
          type: 'quoteData/updateCallbackSurname',
          payload: prospectDetails.surname,
        });
      }

      if (!callbackDetails.contactNumber && prospectDetails.telephoneNumber) {
        dispatch({
          type: 'quoteData/updateCallbackContactNumber',
          payload: prospectDetails.telephoneNumber,
        });
      }
    }
  }, [callbackDetails, prospectDetails, dispatch]);

  if (!show) {
    return null;
  }

  return (
    <div
      id="callback-drawer"
      className="vds-slide-out-drawer vds-slide-out-drawer--active"
      aria-hidden="false"
      aria-expanded="true"
      data-testid={`${fields.testid?.value}-drawer`}
    >
      <button
        type="button"
        className="vds-slide-out-drawer__background"
        onClick={hideOverlay}
        aria-label="Close drawer"
        data-testid={`${fields.testid?.value}-close-drawer-btn`}
      ></button>

      <div className="vds-slide-out-drawer__inner">
        <div className="vds-slide-out-drawer__drawer">
          <button
            type="button"
            onClick={hideOverlay}
            className="vds-slide-out-drawer__close"
            aria-label="Close drawer"
            data-testid={`${fields.testid?.value}-close-drawer-btn-inner`}
          ></button>

          {renderState()}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state, props) => {
  return {
    ...props,
    show: state.appState?.overlay?.type === OverlayConstants.Callback,
    utms: state.utms,
    callbackDetails: state.quoteData?.callbackDetails,
    prospectDetails: state.quoteData?.prospectDetails,
    quoteDetails: state.quoteResponse?.quoteDetails,
    isModalOpen: state.appState?.isModalOpen,
  };
};
Callback.propTypes = {
  fields: PropTypes.shape({
    testid: PropTypes.shape({ value: PropTypes.string }),
    eventName: PropTypes.shape({ value: PropTypes.string }),
    successMessage: PropTypes.shape({ value: PropTypes.string }),
    failureMessage: PropTypes.shape({ value: PropTypes.string }),
    introTitle: PropTypes.shape({ value: PropTypes.string }),
    introStrapLine: PropTypes.shape({ value: PropTypes.string }),
    introFooter: PropTypes.shape({ value: PropTypes.string }),
    formTitle: PropTypes.shape({ value: PropTypes.string }),
    submitButtonText: PropTypes.shape({ value: PropTypes.string }),
    formDisclaimer: PropTypes.shape({ value: PropTypes.string }),
    contactNumber: PropTypes.shape({ value: PropTypes.string }),
    contactNumberText: PropTypes.shape({ value: PropTypes.string }),
    openStatus: PropTypes.shape({ value: PropTypes.string }),
    callBackButtonText: PropTypes.shape({ value: PropTypes.string }),
    questions: PropTypes.arrayOf(PropTypes.shape({ fields: PropTypes.object })),
    formIdLength: PropTypes.shape({ value: PropTypes.number }),
    formIdAcceptedCharacters: PropTypes.shape({ value: PropTypes.string }),
    callbackProject: PropTypes.shape({ value: PropTypes.string }),
    callbackLeadType: PropTypes.shape({ value: PropTypes.string }),
    callbackStagingType: PropTypes.shape({ value: PropTypes.string }),
    callbackWebFormApply: PropTypes.shape({ value: PropTypes.string }),
    callbackWebFormQuote: PropTypes.shape({ value: PropTypes.string }),
  }).isRequired,
  show: PropTypes.bool,
  utms: PropTypes.object,
  callbackDetails: PropTypes.shape({
    forename: PropTypes.string,
    surname: PropTypes.string,
    title: PropTypes.string,
    contactNumber: PropTypes.string,
  }),
  prospectDetails: PropTypes.shape({
    forename: PropTypes.string,
    surname: PropTypes.string,
    title: PropTypes.string,
    telephoneNumber: PropTypes.string,
  }),
  quoteDetails: PropTypes.array,
  isModalOpen: PropTypes.bool,
};

export default connect(mapStateToProps)(Callback);
